import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import {
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { AccountClient } from 'AccountClient'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import { calculateDocAmt } from 'containers/ARAPModule/Helper/AmountCalculation'
import { isBefore } from 'date-fns'
import {
  AcctPermission,
  useGetBillItemFromDepartmentQuery,
  useGetTaxSchemeNewListingQuery,
  useUpdateGenerateArScheduledBillingMutation,
} from 'generated/graphql'
import { calculateTaxDetail } from 'helpers/Calculation/taxCalculationDetail'
import { filterTaxSchemeEffectiveDate } from 'helpers/Filter/filterTaxSchemeEffectiveDate'
import { usePermissionChecker } from 'helpers/Hooks/usePermissionChecker'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { formatDashDate } from 'helpers/StringNumberFunction/FormatDate'
import { amtStr } from 'helpers/StringNumberFunction/NumFormatters'
import { CommonYupValidation } from 'helpers/YupSchema/yup'
import React, { useContext, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'

interface ScheduledBillingProps {
  CompanyID: string
  ScheduledBillingDate: Date
  BillingAmt: number
  BillItemID: string
  TaxSchemeID: string
  Description: string
  TaxAmtDetail: any
  Remark: string
}

export const ARGenerateScheduledBillingForm = (props: any) => {
  let history = useHistory()
  let location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const { setOpenSnackBar, setSnackBarMsg } = useContext(SnackBarContext) as any
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)
  const [openExitConf, setOpenExitConf] = useState(null)
  const [taxAmt, setTaxAmt] = useState(
    !!editData?.TaxAmt ? editData?.TaxAmt : 0
  )
  const [billingAmt, setBillingAmt] = useState(editData?.BillingAmt ?? 0)
  const [selectedTax, setSelectedTax] = useState(null)

  const { handlePermDisabled } = usePermissionChecker()

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const ScheduledBillingSchema = yup.object().shape({
    ScheduledBillingDate: yup
      .date()
      .required('Billing Date is required')
      .test(
        'end-date-test',
        `Billing date cannot be less than today's date`,
        function(value) {
          if (!value) return true
          const selectedDate = new Date(value)
          selectedDate.setHours(0, 0, 0, 0)

          const today = new Date()
          today.setHours(0, 0, 0, 0)

          return !isBefore(selectedDate, today)
        }
      ),
    BillingAmt: yup
      .number()
      .transform((originalValue, originalObject) => {
        if (originalValue?.length > 3) {
          return parseInt(originalObject.replace(/,/g, ''))
        } else {
          return parseFloat(originalObject)
        }
      })
      .min(0.0001, 'Billing Amount must be at least 0.0001')
      .required('Billing Amount is required'),
    BillItemID: CommonYupValidation.requireField(SystemMsgs.billingItem()),
    TaxSchemeID: CommonYupValidation.requireField(SystemMsgs.tax()),
    Description: CommonYupValidation.requireField(SystemMsgs.description()),
  })

  const {
    handleSubmit,
    register,
    setValue,
    control,
    errors,
    watch,
    getValues,
    clearErrors,
    formState: { isDirty },
  } = useForm<ScheduledBillingProps>({
    defaultValues: {},
    mode: 'onSubmit',
    resolver: yupResolver(ScheduledBillingSchema),
  })

  const handleUnitPriceChange = event => {
    setBillingAmt(event.floatValue)
    setValue('BillingAmt', event?.floatValue)
  }

  /* -------------------------------------------- */
  /*                    QUEARY                    */
  /* -------------------------------------------- */

  const [
    updateGenerateARScheduledBilling,
    { loading: updateGenerateARScheduledBillingLoading },
  ] = useUpdateGenerateArScheduledBillingMutation({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      history.push(
        `/account-receivable/${CompanyID}/scheduled-billing/generate-scheduled-billing`
      )
      setSnackBarMsg(SystemMsgs.updateRecord())
      setOpenSnackBar(true)
    },
  })

  const {
    loading: billItemLoading,
    data: { getBillItemFromDepartment } = { getBillItemFromDepartment: [] },
  } = useGetBillItemFromDepartmentQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
      CostCentreID: editData?.ScheduledBillingHeader?.CostCentre?.CostCentreID,
    },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  const {
    loading: taxSchemeLoading,
    data: { getTaxSchemeListing } = {
      getTaxSchemeListing: [],
    },
  } = useGetTaxSchemeNewListingQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      if (editData?.TaxSchemeID) {
        let filteredTaxScheme = filterTaxSchemeEffectiveDate(
          editData?.ScheduledBillingDate,
          getTaxSchemeListing
        )

        let selectTax = filteredTaxScheme.find(
          x => x?.id === editData?.TaxSchemeID
        )
        setSelectedTax(selectTax)
        setValue('TaxSchemeID', selectTax?.id)
        clearErrors('TaxSchemeID')

        initializeTaxDetailsInEditMode(selectTax)

        let recalculateCost =
          selectTax?.isExclusive === true
            ? editData?.DocAmt - editData?.TaxAmt
            : editData?.DocAmt

        setBillingAmt(recalculateCost)
        setValue('BillingAmt', recalculateCost)
      }
    },
    client: AccountClient,
    variables: { TaxClass: 'OUTPUT' },
  })

  const taxSchemeOutput = filterTaxSchemeEffectiveDate(
    watch('ScheduledBillingDate'),
    getTaxSchemeListing
  )

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */

  const submitFooterOption = {
    name: 'Save',
    onClick: () => {
      handleSubmit(data => onSubmit(data, 'submit'))()
    },
    color: 'primary',
    props: {
      type: 'submit',
      disabled: handlePermDisabled({
        companyID: CompanyID,
        permEnum: AcctPermission.AccReceivableGenerateScheduledBillingUpdate,
      }),
    },
  }
  let footerOptions = submitFooterOption

  /* -------------------------------------------- */
  /*                    SUBMIT                    */
  /* -------------------------------------------- */

  const onSubmit = (data, status) => {
    const todayDateString = formatDashDate(new Date().toISOString())
    if (data?.ScheduledBillingDate) {
      const scheduledBillingDateString = formatDashDate(
        new Date(data.ScheduledBillingDate)?.toISOString()
      )
      if (scheduledBillingDateString < todayDateString) {
        setErrMessage("Billing date cannot be less than today's date")
        setErrDialog(true)
        return
      }
    }
    const formattedTaxDetails = result.taxDetails.map((tax, idx) => {
      return {
        tax_amt: Number(watch(`TaxAmtDetail[${idx}]`)) ?? tax.TaxAmount,
        tax_rate: tax.TaxRate * 100,
        tax_id: tax.taxId,
        tax_scheme_detail_id: tax.taxSchemeDetailId,
        tax_scheme_id: tax.taxSchemeId,
      }
    })

    updateGenerateARScheduledBilling({
      variables: {
        CompanyID,
        ScheduledBillingItemID: editData?.ScheduledBillingItemID,
        input: {
          ScheduledBillingDate: formatDashDate(
            new Date(data?.ScheduledBillingDate)?.toISOString()
          ),
          BillItemID: data?.BillItemID,
          TaxSchemeID: data?.TaxSchemeID,
          TaxRate: 0,
          TaxAmt: Number(taxAmt),
          DocAmt: Number(docAmt),
          BillingAmt: Number(docAmt) - Number(taxAmt),
          Description: data?.Description,
          Remark: data?.Remark,
        },
        itemTaxInput: formattedTaxDetails,
      },
    })
  }

  // ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ - TAX CALCULATION - ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ //
  const prepareTaxCalculationData = (selectedTax: any) => {
    if (!selectedTax?.taxSchemeDetailList[0]?.taxSchemeDetailItemList?.length) {
      return []
    }

    const taxSchemeDetail = selectedTax?.taxSchemeDetailList[0]

    return taxSchemeDetail.taxSchemeDetailItemList.map(item => ({
      taxId: item.taxId,
      taxSchemeId: taxSchemeDetail.taxSchemeId,
      taxSchemeDetailId: taxSchemeDetail.id,
      seqNo: item.seqNo,
      taxOnSeq: item.taxOnSeq || [],
      computeMethod:
        item.computeMethod === 'TAX_ON_GROSS'
          ? 'Tax_On_Gross'
          : 'Tax_On_Taxed_Price',
      taxRate: item.taxRate,
      taxCode: item.taxInfo?.code,
    }))
  }

  const calculateTaxFromState = (selectedTax: any, amount: number) => {
    const taxData = prepareTaxCalculationData(selectedTax)

    const isExclusive = Boolean(selectedTax?.isExclusive)

    return calculateTaxDetail(taxData, amount, isExclusive)
  }
  let result = calculateTaxFromState(selectedTax, billingAmt)

  const initializeTaxDetailsInEditMode = selectTax => {
    if (editData?.TaxAmtDetails && Array.isArray(editData?.TaxAmtDetails)) {
      editData.TaxAmtDetails.forEach((taxDetail, index) => {
        register(`TaxAmtDetail[${index}]`)
        setValue(`TaxAmtDetail[${index}]`, taxDetail.taxAmount)
      })
      setValue('taxDetailsCount', editData.TaxAmtDetails.length)
    } else if (selectTax) {
      const currentAmount = Number(
        selectTax?.isExclusive === true
          ? editData?.BillingAmt
          : editData?.DocAmt
      )
      const taxResult = calculateTaxFromState(selectTax, currentAmount)
      if (taxResult?.taxDetails) {
        updateTaxDetailsInForm(taxResult.taxDetails, taxResult.TaxAmount)
      }
    }
  }

  const updateTaxDetailsInForm = (taxDetails, taxAmount) => {
    if (!taxDetails || taxDetails.length === 0) return
    const prevTaxDetailsCount = watch('taxDetailsCount') || 0
    taxDetails.forEach((tax, index) => {
      register(`TaxAmtDetail[${index}]`)
      setValue(`TaxAmtDetail[${index}]`, tax.TaxAmount)
    })
    setValue('taxDetailsCount', taxDetails.length)
    if (prevTaxDetailsCount > taxDetails.length) {
      for (let i = taxDetails.length; i < prevTaxDetailsCount; i++) {
        setValue(`TaxAmtDetail[${i}]`, null)
      }
    }
    setValue('TaxAmt', taxAmount)
    setTaxAmt(taxAmount)
  }

  const docAmt = selectedTax?.isExclusive
    ? calculateDocAmt(billingAmt, taxAmt)
    : Number(billingAmt)

  const handleTaxChange = e => {
    const currentTax = taxSchemeOutput.find(x => x?.id === e)
    const taxResult = calculateTaxFromState(currentTax, billingAmt)
    updateTaxDetailsInForm(taxResult?.taxDetails, taxResult?.TaxAmount)
    setSelectedTax(taxSchemeOutput?.find(x => x?.id === e))
    setTaxAmt(taxResult?.TaxAmount)
  }

  return (
    <>
      {taxSchemeLoading && <Loading />}
      {updateGenerateARScheduledBillingLoading && <Loading />}
      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/account-receivable/${CompanyID}/scheduled-billing/generate-scheduled-billing`
          )
        }}
        smTitle={'Account Receivable'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main' },
          { name: 'Schedule Billing' },
          { name: 'Generate Schedule Billing', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'approve-reject'
                ? 'Approve/Reject'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents>
          {!billItemLoading && (
            <Controller
              render={({ onChange, onBlur, value }) => {
                const defVal = getBillItemFromDepartment?.filter(
                  x => x?.BillItemID === editData?.BillItemID
                )[0]

                return (
                  <Autocomplete
                    options={
                      getBillItemFromDepartment?.sort((a, b) => {
                        return a.Name.localeCompare(b.Name)
                      }) || []
                    }
                    getOptionLabel={option =>
                      `${option?.Name} | ${option?.Description}`
                    }
                    fullWidth
                    onChange={(value, newValue: any, reason) => {
                      setValue('BillItemID', newValue?.BillItemID)
                      if (newValue?.TaxSchemeID) {
                        register('TaxSchemeID')
                        setValue('TaxSchemeID', newValue?.TaxSchemeID)
                      }
                    }}
                    renderOption={(props, option) => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">{props?.Name}</span>
                          </div>
                          <div className="desc">{props?.Description}</div>
                        </div>
                      )
                    }}
                    defaultValue={defVal}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            required
                            label="Billing Item"
                            variant="standard"
                            fullWidth
                            name="BillItemID"
                            helperText={errors?.BillItemID?.message}
                            error={errors?.BillItemID ? true : false}
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              name="BillItemID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="dense"
              ref={register}
              required
              defaultValue={editData ? editData?.BillItemID : ''}
              helperText={errors?.BillItemID?.message}
              error={errors?.BillItemID ? true : false}
            />
          )}
          <Controller
            as={KeyboardDatePicker}
            name="ScheduledBillingDate"
            required
            label="Billing Date"
            control={control}
            format="dd/MM/yyyy"
            margin="normal"
            allowKeyboardControl
            ref={register}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
            showTodayButton
            className="left"
            value={new Date()}
            defaultValue={
              editData ? editData?.ScheduledBillingDate : new Date()
            }
            helperText={errors?.ScheduledBillingDate?.message}
            error={errors?.ScheduledBillingDate ? true : false}
            onChange={(e: Date | null) => {}}
          />

          <Controller
            name="BillingAmt"
            control={control}
            defaultValue={billingAmt}
            rules={{ required: true }}
            className="right"
            render={({ value }) => (
              <NumberFormat
                {...value}
                value={billingAmt}
                onValueChange={e => handleUnitPriceChange(e)}
                customInput={TextField}
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                margin="normal"
                label="Billing Amount"
                id="standard-basic"
                className="right"
                disabled
                helperText={errors?.BillingAmt?.message}
                error={errors?.BillingAmt ? true : false}
              />
            )}
            helperText={errors?.BillingAmt?.message}
            error={errors?.BillingAmt ? true : false}
          />

          <Controller
            as={
              <TextField
                label="Tax Scheme"
                required
                fullWidth
                SelectProps={{
                  renderValue: () =>
                    `${selectedTax?.name ?? ''} | ${selectedTax?.description ??
                      ''} ${selectedTax?.isExclusive ? `(E)` : `(I)`}`,
                  onChange: event => {
                    handleTaxChange(event?.target?.value)
                    setValue('TaxSchemeID', event?.target?.value.toString())
                    clearErrors('TaxSchemeID')
                  },
                }}
                select
                helperText={errors?.TaxSchemeID?.message}
                error={errors?.TaxSchemeID ? true : false}
              >
                <ListSubheader>Output</ListSubheader>
                {taxSchemeOutput?.map((taxScheme, index) => (
                  <MenuItem id="tax-select" key={index} value={taxScheme?.id}>
                    {
                      <div className="content-wrap full">
                        <div>
                          <span className="xsTitle">{taxScheme?.name}</span>
                        </div>
                        <div className="desc">
                          {taxScheme?.description}{' '}
                          {taxScheme?.isExclusive ? `(E)` : `(I)`}
                        </div>
                      </div>
                    }
                  </MenuItem>
                ))}
              </TextField>
            }
            select
            name="TaxSchemeID"
            autoComplete="off"
            control={control}
            defaultValue={editData?.TaxSchemeID || ''}
            ref={register}
          />

          {result.taxDetails.length > 0 && (
            <Grid item xs={12}>
              <Paper
                elevation={1}
                style={{
                  padding: '1px 7px',
                  borderRadius: '2px',
                  backgroundColor: '#fff9f2',
                }}
              >
                {result.taxDetails.map((tax, index) => (
                  <Grid container key={index} spacing={1} alignItems="center">
                    <Grid item xs={4}>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: '0.63rem',
                          fontWeight: 500,
                          lineHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {tax?.taxCode || `Tax ${index + 1}`}
                      </Typography>
                    </Grid>

                    <Grid item xs={3}>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: '0.63rem',
                          fontWeight: 500,
                          lineHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {typeof tax?.TaxRate === 'number'
                          ? `${(tax.TaxRate * 100).toFixed(2)}%`
                          : `N/A`}
                      </Typography>
                    </Grid>

                    <Grid item xs={5}>
                      <Controller
                        name={`TaxAmtDetail[${index}]`}
                        control={control}
                        defaultValue={tax.TaxAmount}
                        render={({ onChange, value }) => (
                          <NumberFormat
                            customInput={TextField}
                            fullWidth
                            className="disabled-input"
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            style={{ marginTop: '10px', fontSize: '0.63rem' }}
                            allowNegative={false}
                            size="small"
                            value={
                              watch(`TaxAmtDetail[${index}]`) ?? tax.TaxAmount
                            }
                            InputProps={{
                              disableUnderline: true,
                              readOnly: true,
                              style: {
                                fontSize: '0.63rem',
                                height: '23px',
                                padding: '0 4px',
                              },
                            }}
                            InputLabelProps={{ style: { display: 'none' } }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Paper>
            </Grid>
          )}

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            ref={register()}
            control={control}
            margin="normal"
            name="TaxAmount"
            label="Tax Amount"
            onValueChange={e => {
              setTaxAmt(e?.floatValue)
            }}
            value={taxAmt}
            defaultValue={editData?.TaxAmt ?? taxAmt ?? 0}
            decimalScale={2}
            fixedDecimalScale
            className="left"
            disabled
            InputProps={{ disableUnderline: true, readOnly: true }}
          />

          <Controller
            render={({ value, onChange }) => {
              value = { docAmt }
              return (
                <TextField
                  margin="normal"
                  label="Doc Amount"
                  value={
                    selectedTax?.isExclusive
                      ? amtStr(calculateDocAmt(billingAmt, taxAmt))
                      : amtStr(billingAmt)
                  }
                  InputProps={{ readOnly: true, disableUnderline: true }}
                  className="right"
                  disabled
                />
              )
            }}
            disabled
            margin="normal"
            defaultValue={(docAmt || 0) ?? (editData?.DocAmt || 0)}
            name="DocAmt"
            ref={register()}
            control={control}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="Description"
            label="Description"
            defaultValue={editData ? editData?.Description : ''}
            autoComplete="off"
            control={control}
            fullWidth
            margin="normal"
            ref={register}
            required
            helperText={errors?.Description?.message}
            error={errors?.Description ? true : false}
            multiline
          />
          <Controller
            as={TextField}
            id="standard-basic"
            name="Remark"
            label="Billing Remark"
            defaultValue={editData ? editData?.Remark : ''}
            autoComplete="off"
            control={control}
            fullWidth
            margin="normal"
            ref={register}
            multiline
          />
        </CardContents>
      </ContentWrapper>

      <AccountFooter options={[footerOptions]} />

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(`/general-ledger/${CompanyID}/journal-processing`)
          localStorage.removeItem('assetsRegistration')
          localStorage.removeItem('usedDepartment')
          localStorage.removeItem('attachment')
        }}
      />
    </>
  )
}
